import { useEffect, useState } from "react";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * TableOfContents — sticky sidebar of section headings.
 * Provide `items = [{id, label}]`. If null, auto-detects h2[id] under `containerSelector`.
 */
const TableOfContents = ({ items, containerSelector = "article" }) => {
  const [detected, setDetected] = useState([]);
  const [active, setActive] = useState("");

  useEffect(() => {
    if (items) return;
    const root = document.querySelector(containerSelector);
    if (!root) return;
    const hs = Array.from(root.querySelectorAll("h2")).filter((h) => h.textContent.trim());
    const list = hs.map((h) => {
      if (!h.id) h.id = slugify(h.textContent);
      return { id: h.id, label: h.textContent.trim() };
    });
    setDetected(list);
  }, [items, containerSelector]);

  const list = items || detected;

  useEffect(() => {
    if (list.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    list.forEach((i) => {
      const el = document.getElementById(i.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [list]);

  if (list.length < 2) return null;

  return (
    <nav aria-label="Table of contents" data-testid="table-of-contents" className="hidden lg:block sticky top-24 self-start">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-4">On this page</div>
      <ul className="space-y-2.5 text-sm border-l border-border pl-4">
        {list.map((i) => (
          <li key={i.id}>
            <a
              href={`#${i.id}`}
              className={`block leading-snug transition-colors ${
                active === i.id ? "text-[hsl(var(--primary))]" : "text-muted-foreground hover:text-[hsl(var(--primary))]"
              }`}
            >
              {i.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default TableOfContents;
